import { measure, sameRect, stillAnimating } from "./geometry";

// How long scrolling has to be quiet before a new anchor is committed.
const SCROLL_QUIET = 160;
// How many identical measurements in a row commit an anchor. Two was enough
// for an anchor that simply had to appear, and not enough for one whose own
// content lands a frame later.
const AGREEING_READS = 3;
// How long the outgoing ring takes to fade away.
export const SPOTLIGHT_FADE = 90;

/**
 * Tracks an anchor's viewport rect and only ever reports a destination it is
 * confident about. Imperative port of the prototype's useSettledAnchor.
 *
 * Two rules make it work:
 *
 * 1. A new anchor is not committed until several reads agree AND scrolling
 *    has been quiet — beats scroll their anchor into view, and committing
 *    early sends the card chasing a destination that is itself in motion.
 * 2. Once committed, tracking goes live so ordinary scrolling, resizing and
 *    turbo-frame/stream replacement keep the spotlight on the anchor (the
 *    poll re-queries the DOM by selector every tick, so a replaced element
 *    is re-acquired).
 *
 * Callbacks:
 *   onCommit(rect, outgoing) - the anchor settled (outgoing = previous rect,
 *                              kept SPOTLIGHT_FADE ms for the cross-fade)
 *   onTrack(rect)            - live update of a committed anchor
 *   onTimeout()              - the anchor never appeared within `timeout` ms
 */
export class AnchorSettler {
  constructor(anchor, { square = false, previousRect = null, onCommit, onTrack, onTimeout, timeout = 4000 } = {}) {
    this.anchor = anchor;
    this.square = square;
    this.previousRect = previousRect;
    this.onCommit = onCommit;
    this.onTrack = onTrack;
    this.onTimeout = onTimeout;

    this.settled = false;
    this.committedRect = null;
    this.candidate = null;
    this.agreed = 0;
    // Treat the beat change itself as scroll activity: the beat may be about
    // to scroll its anchor into view, so never commit on the first reads.
    this.lastScroll = Date.now();

    this.remeasure = this.remeasure.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    this.frame = window.requestAnimationFrame(this.remeasure);
    // Anchors move while a screen settles — fonts load, sections expand, the
    // beat scrolls them into view. Poll until reads agree, then track live.
    this.poll = window.setInterval(this.remeasure, 60);
    this.timeoutTimer = window.setTimeout(() => {
      window.clearInterval(this.poll);
      this.poll = null;
      if (!this.settled && this.onTimeout) this.onTimeout();
    }, timeout);

    window.addEventListener("resize", this.remeasure);
    window.addEventListener("scroll", this.handleScroll, true);

    this.observer = new ResizeObserver(this.remeasure);
    this.observer.observe(document.body);
  }

  handleScroll() {
    if (!this.settled) {
      // While acquiring, any scroll restarts the agreeing pair — an anchor
      // already on screen would otherwise commit and then be dragged by the
      // beat's own smooth scroll.
      this.lastScroll = Date.now();
      this.candidate = null;
      this.agreed = 0;
      return;
    }
    this.remeasure();
  }

  remeasure() {
    if (this.destroyed) return;

    const next = measure(this.anchor, this.square);
    if (!next) return;

    if (!this.settled && stillAnimating(this.anchor)) {
      this.candidate = null;
      this.agreed = 0;
      return;
    }

    if (this.settled) {
      // Live tracking of an anchor already committed — no cross-fade.
      this.committedRect = next;
      if (this.onTrack) this.onTrack(next);
      return;
    }

    // Matching reads AND no scrolling for a beat — either alone is not
    // enough, because a smooth scroll can pause long enough for reads to
    // agree before it has finished.
    const quiet = Date.now() - this.lastScroll > SCROLL_QUIET;
    this.agreed = sameRect(this.candidate, next) ? this.agreed + 1 : 0;

    if (quiet && this.agreed >= AGREEING_READS - 1) {
      this.settled = true;
      this.committedRect = next;
      window.clearTimeout(this.timeoutTimer);
      // Skip the fade when the box has not really moved.
      const outgoing = sameRect(this.previousRect, next) ? null : this.previousRect;
      if (this.onCommit) this.onCommit(next, outgoing);
    }
    this.candidate = next;
  }

  destroy() {
    this.destroyed = true;
    window.cancelAnimationFrame(this.frame);
    if (this.poll) window.clearInterval(this.poll);
    window.clearTimeout(this.timeoutTimer);
    window.removeEventListener("resize", this.remeasure);
    window.removeEventListener("scroll", this.handleScroll, true);
    if (this.observer) this.observer.disconnect();
  }
}
